import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, MapPin, Search } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../ui/dialog';
import { useToast } from '../../../hooks/use-toast';
import LoadingRing from '../../ui-components/LoadingRing';
import { Category } from '../../ui-components/category/categoryColors';
import { CategoryChip } from '../../ui-components/category/CategoryChip';
import {
    getRoomsForPolicy,
    linkRoomToPolicy,
    unlinkRoomFromPolicy,
} from '../../../services/api/policyService';
import { getAllMRIMachinesByDepartment } from '../../../services/apiService';
import { MRIMachinePopulated } from '../../room-overview/types';
import { FieldStrengthTile } from '../../room-overview/FieldStrengthChip';
import EquipmentTypeBadge from '../../room-overview/equipment/EquipmentTypeBadge';

interface PolicyRoomsDialogProps {
    policyId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    canManage: boolean;
    onCountChange?: (count: number) => void;
}

interface EquipmentRow {
    _id: string;
    name: string;
    type: string;
}

interface RoomRow {
    _id: string;
    name: string;
    model: string;
    manufacturer?: string;
    fieldStrength: number;
    location?: string;
    category?: Category;
    equipment: EquipmentRow[];
}

const targetKey = (machineId: string, equipmentId?: string) =>
    equipmentId ? `${machineId}:${equipmentId}` : machineId;

const byName = (a: string, b: string) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' });

const matches = (room: RoomRow, term: string) =>
    !term ||
    room.name.toLowerCase().includes(term) ||
    room.model.toLowerCase().includes(term) ||
    room.manufacturer?.toLowerCase().includes(term) ||
    room.location?.toLowerCase().includes(term) ||
    room.category?.name.toLowerCase().includes(term) ||
    room.equipment.some(
        (item) =>
            item.name.toLowerCase().includes(term) ||
            item.type.toLowerCase().includes(term)
    );

export default function PolicyRoomsDialog({
    policyId,
    open,
    onOpenChange,
    canManage,
    onCountChange,
}: PolicyRoomsDialogProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [rooms, setRooms] = useState<RoomRow[]>([]);
    const [linked, setLinked] = useState<Set<string>>(new Set());
    const [pending, setPending] = useState<Set<string>>(new Set());
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchRooms = async () => {
        setLoading(true);
        setError(null);
        try {
            const links = await getRoomsForPolicy(policyId);
            const keys = new Set(
                links.flatMap((room) => [
                    ...(room.linkedOnRoom ? [targetKey(room._id)] : []),
                    ...room.equipment.map((item) =>
                        targetKey(room._id, item._id)
                    ),
                ])
            );
            setLinked(keys);
            onCountChange?.(keys.size);
            setExpanded(new Set(links.map((room) => room._id)));

            const source: RoomRow[] = canManage
                ? (
                      (await getAllMRIMachinesByDepartment()) as MRIMachinePopulated[]
                  ).map((room) => ({
                      _id: room._id,
                      name: room.Name,
                      model: room.Model,
                      manufacturer: room.Manufacturer,
                      fieldStrength: room.FieldStrength,
                      location: room.location,
                      category: room.category,
                      equipment: (room.equipment ?? []).map((item) => ({
                          _id: item._id,
                          name: item.name,
                          type: item.type,
                      })),
                  }))
                : links.map((room) => ({
                      _id: room._id,
                      name: room.Name,
                      model: room.Model,
                      manufacturer: room.Manufacturer,
                      fieldStrength: room.FieldStrength,
                      location: room.location,
                      category: room.category,
                      equipment: room.equipment,
                  }));
            setRooms(source);
        } catch (err) {
            console.error('Failed to fetch rooms:', err);
            setError(t('departmentPolicies.roomsDialog.failedToLoad'));
            toast({
                title: t('departmentPolicies.roomsDialog.failedToLoad'),
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchRooms();
            setSearch('');
        }
    }, [open, policyId]);

    const toggle = async (machineId: string, equipmentId?: string) => {
        const key = targetKey(machineId, equipmentId);
        const isLinked = linked.has(key);
        setPending((prev) => new Set(prev).add(key));
        try {
            const ok = isLinked
                ? await unlinkRoomFromPolicy(policyId, machineId, equipmentId)
                : await linkRoomToPolicy(policyId, machineId, equipmentId);
            if (!ok) {
                throw new Error('Room link request failed');
            }
            setLinked((prev) => {
                const next = new Set(prev);
                if (isLinked) next.delete(key);
                else next.add(key);
                onCountChange?.(next.size);
                return next;
            });
        } catch (err) {
            console.error('Failed to update room link:', err);
            toast({
                title: t('departmentPolicies.roomsDialog.failedToUpdate'),
                variant: 'destructive',
            });
        } finally {
            setPending((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    };

    const toggleExpanded = (roomId: string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(roomId)) next.delete(roomId);
            else next.add(roomId);
            return next;
        });

    const term = search.trim().toLowerCase();
    const visibleRooms = rooms
        .filter((room) => matches(room, term))
        .sort((a, b) => byName(a.name, b.name));
    const linkedKeys = [...linked];
    const linkedRoomCount = linkedKeys.filter(
        (key) => !key.includes(':')
    ).length;
    const linkedItemCount = linkedKeys.length - linkedRoomCount;

    const renderRoom = (room: RoomRow) => {
        const roomKey = targetKey(room._id);
        const isExpanded = term !== '' || expanded.has(room._id);
        const equipment = canManage
            ? room.equipment
            : room.equipment.filter((item) =>
                  linked.has(targetKey(room._id, item._id))
              );
        return (
            <li
                key={room._id}
                className={cn(
                    'border-b border-subtle last:border-b-0',
                    isExpanded && 'bg-muted'
                )}
            >
                <div
                    className={cn(
                        'flex items-center gap-4 px-4 py-3 transition-colors',
                        !isExpanded && 'hover:bg-muted'
                    )}
                >
                    <Checkbox
                        id={`room-link-${roomKey}`}
                        checked={linked.has(roomKey)}
                        disabled={!canManage || pending.has(roomKey)}
                        onCheckedChange={() => toggle(room._id)}
                        aria-label={t(
                            'departmentPolicies.roomsDialog.entireRoomAria',
                            { name: room.name }
                        )}
                    />
                    <button
                        type="button"
                        onClick={() => toggleExpanded(room._id)}
                        aria-expanded={isExpanded}
                        className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    >
                        <FieldStrengthTile fieldStrength={room.fieldStrength} />
                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                            <span className="flex min-w-0 items-center gap-2">
                                <span className="truncate text-sm font-semibold tracking-tight">
                                    {room.name}
                                </span>
                                {room.category && (
                                    <CategoryChip category={room.category} />
                                )}
                            </span>
                            <span className="flex min-w-0 items-center gap-3 text-xs font-medium text-subtle-foreground">
                                <span className="truncate">
                                    {[room.manufacturer, room.model]
                                        .filter(Boolean)
                                        .join(' ')}
                                </span>
                                {room.location && (
                                    <span className="flex min-w-0 shrink-0 items-center gap-1">
                                        <MapPin
                                            className="h-3 w-3 shrink-0"
                                            aria-hidden="true"
                                        />
                                        <span className="truncate">
                                            {room.location}
                                        </span>
                                    </span>
                                )}
                            </span>
                        </span>
                        <span className="shrink-0 text-xs font-medium text-subtle-foreground">
                            {t('roomOverview.machineCard.itemCount', {
                                count: room.equipment.length,
                            })}
                        </span>
                        <ChevronDown
                            aria-hidden="true"
                            className={cn(
                                'h-4 w-4 text-subtle-foreground motion-safe:transition-transform',
                                isExpanded && 'rotate-180'
                            )}
                        />
                    </button>
                </div>
                {isExpanded && (
                    <div className="pb-4 pl-[5.75rem] pr-4 pt-1">
                        {equipment.length > 0 ? (
                            <ul className="divide-y divide-subtle overflow-hidden rounded-md border border-subtle bg-card">
                                {equipment.map((item) => {
                                    const key = targetKey(room._id, item._id);
                                    const id = `room-link-${key}`;
                                    return (
                                        <li
                                            key={key}
                                            className="flex items-center gap-3 px-4 py-2.5"
                                        >
                                            <Checkbox
                                                id={id}
                                                checked={linked.has(key)}
                                                disabled={
                                                    !canManage ||
                                                    pending.has(key)
                                                }
                                                onCheckedChange={() =>
                                                    toggle(room._id, item._id)
                                                }
                                            />
                                            <label
                                                htmlFor={id}
                                                className="min-w-0 flex-1 cursor-pointer truncate text-sm text-foreground"
                                            >
                                                {item.name}
                                            </label>
                                            <EquipmentTypeBadge
                                                type={item.type}
                                            />
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p className="text-xs font-medium text-subtle-foreground">
                                {t(
                                    'departmentPolicies.roomsDialog.noEquipment'
                                )}
                            </p>
                        )}
                    </div>
                )}
            </li>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] max-w-3xl">
                <DialogHeader>
                    <DialogTitle>
                        {t('departmentPolicies.roomsDialog.title')}
                    </DialogTitle>
                    <DialogDescription>
                        {canManage
                            ? t(
                                  'departmentPolicies.roomsDialog.descriptionAdmin'
                              )
                            : t(
                                  'departmentPolicies.roomsDialog.descriptionViewer'
                              )}
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <LoadingRing size="small" />
                    </div>
                ) : error ? (
                    <p className="py-4 text-sm text-destructive">{error}</p>
                ) : (
                    <div className="flex h-[60vh] min-w-0 flex-col gap-3">
                        {canManage && (
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle-foreground" />
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={t(
                                        'departmentPolicies.roomsDialog.searchPlaceholder'
                                    )}
                                    className="pl-9"
                                />
                            </div>
                        )}
                        <span className="text-xs font-medium text-subtle-foreground">
                            {t('roomOverview.roomOverview.sections.roomCount', {
                                count: linkedRoomCount,
                            })}
                            {' · '}
                            {t('roomOverview.machineCard.itemCount', {
                                count: linkedItemCount,
                            })}
                        </span>
                        <div className="min-h-0 flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                            {visibleRooms.length === 0 ? (
                                <p className="py-4 text-sm font-medium text-subtle-foreground">
                                    {t(
                                        rooms.length === 0
                                            ? canManage
                                                ? 'departmentPolicies.roomsDialog.noRooms'
                                                : 'departmentPolicies.roomsDialog.notLinked'
                                            : 'departmentPolicies.roomsDialog.noMatches'
                                    )}
                                </p>
                            ) : (
                                <ul className="overflow-hidden rounded-lg border border-subtle bg-card">
                                    {visibleRooms.map(renderRoom)}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button onClick={() => onOpenChange(false)}>
                        {t('departmentPolicies.roomsDialog.done')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
